import { Injectable } from '@angular/core';
import { BaseService } from '../services';
import { PaginationModel, RequestParamModel } from '../models';
import { catchError, map, Observable, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class MilestoneStatusService {

 constructor(private baseService: BaseService) { }

 getMilestoneStatus(requestParamModel: RequestParamModel, orderId: number, type?: any): any {
  const url = type ? `status/${type}/${orderId}` : `status/${orderId}`;

  return this.baseService.get(url, requestParamModel).pipe(
    map<any, any>((response) => {
      const milestoneStatus = new PaginationModel<any>();
      const headers = response.headers;
      milestoneStatus.setHeaders(headers);
      milestoneStatus.data = response.body.data as any[];
      return milestoneStatus;
    }),
    catchError((err) => throwError(() => err))
  );
}

changeStatus(orderModel: any): Observable<number> {
    if (orderModel.id !== undefined) {
      return this.baseService.patch('quote/update-sample-request-status/' + orderModel.id, orderModel)
        .pipe(map<any, number>(response => {
          orderModel = response.data as any;
          return orderModel.id;
         }),
        catchError(err => throwError(() => err))
    );
    } else {
      return this.baseService.post('quote/update-sample-request-status', orderModel)
        .pipe(map<any, number>(response => {
          orderModel = response.data as any;
          return orderModel.id;
        }),
        catchError(err => throwError(() => err))
    );
    }
  }

  /**
   * {@link changeStatus} for a sample request that already exists, answering the
   * saved request itself: a return to Samples Packaged carries
   * `unleashed_sync_started`, which says to ask how its Unleashed order took it.
   */
  changeSampleRequestStatus(orderModel: any): Observable<any> {
    return this.baseService.patch('quote/update-sample-request-status/' + orderModel.id, orderModel)
      .pipe(map<any, any>(response => response.data),
        catchError(err => throwError(() => err))
      );
  }

  /**
   * Move a sample request back to an earlier milestone.
   *
   * `PATCH quote/move-back-sample-request-status/{id}` — body `{quote_status}`,
   * the short name of the milestone to return to. api-v2 keeps that milestone's
   * date and user, clears every later one (its status history and the activity
   * rows stamped with it) and refuses Cancelled, Archived, Converted, Unconverted and
   * Conversion Pending requests with a 422 that says why.
   */
  moveSampleRequestBack(quoteId: any, quoteStatus: string): Observable<any> {
    return this.baseService
      .patch('quote/move-back-sample-request-status/' + quoteId, {quote_status: quoteStatus})
      .pipe(map<any, any>(response => response.data),
        catchError(err => throwError(() => err))
      );
  }

  /**
   * Change only the expected-ready date on an already-delayed sample request.
   *
   * `PATCH quote/update-sample-expected-ready-date/{id}` — body
   * `{expected_ready_date}` in `YYYY-MM-DD`. api-v2 validates it with
   * `createDateValidator('expected_ready_date', true)` and refuses a date
   * before today on the Sydney business day.
   *
   * Marking a request delayed in the first place goes through
   * `changeStatus()` instead, which carries `quote_status` alongside the date
   * — the status route's validator requires the date if and only if the
   * status is `SDLY`.
   */
  updateSampleExpectedReadyDate(quoteId: any, expectedReadyDate: string, internalNote = ''): Observable<any> {
    // `internal_note` is optional: sent, it rewrites the delay's existing note
    // (or creates one); left out, the note is untouched.
    const body: any = {expected_ready_date: expectedReadyDate};
    if (internalNote) {
      body.internal_note = internalNote;
    }
    return this.baseService
      .patch('quote/update-sample-expected-ready-date/' + quoteId, body)
      .pipe(map<any, any>(response => response.data),
        catchError(err => throwError(() => err))
      );
  }

  /**
   * Change the reason on a request already marked Failed to Reach Destination.
   *
   * `PATCH quote/update-sample-failed-reason/{id}` — body `{failed_reason, internal_note?}`.
   * The status does not move; a changed reason gets its own History row, and a note is added
   * to Internal notes against the status. Marking it failed, and the undo, go through
   * `changeStatus()` (`quote_status` SFAIL / SDESP).
   */
  updateSampleFailedReason(quoteId: any, failedReason: string, internalNote = ''): Observable<any> {
    const body: any = {failed_reason: failedReason};
    if (internalNote) {
      body.internal_note = internalNote;
    }
    return this.baseService
      .patch('quote/update-sample-failed-reason/' + quoteId, body)
      .pipe(map<any, any>(response => response.data),
        catchError(err => throwError(() => err))
      );
  }

  /**
   * Move an ACTIVE Samples Delayed from a sample request onto the follow-on that
   * Split Delivery just created out of it.
   *
   * `PATCH quote/transfer-sample-delay/{originId}` — body `{target_quote_id}`.
   * Both halves (the follow-on takes the status, the expected-ready date, the
   * delay note and the follow-up task; this request goes back to Samples
   * Requested with its task closed) commit as ONE transaction server-side, so
   * the console never has to sequence them or clean up half a move.
   *
   * Safe to call on every split: a request with no delay to move is a quiet 200
   * that writes nothing, and the API refuses any target that is not the request
   * `split_from` this one — so the delay can only ever follow the balance.
   */
  transferSampleDelay(originId: any, targetQuoteId: any): Observable<any> {
    return this.baseService
      .patch('quote/transfer-sample-delay/' + originId, {target_quote_id: targetQuoteId})
      .pipe(map<any, any>(response => response.data),
        catchError(err => throwError(() => err))
      );
  }

  changeQuoteStatus(orderModel: any): Observable<number> {
    return this.baseService.patch('quote/update-quote-status/' + orderModel.id, orderModel)
      .pipe(map<any, number>(response => {
          orderModel = response.data as any;
          return orderModel.id;
        }),
        catchError(err => throwError(() => err))
      );
  }

  changeOrderStatus(orderModel: any): Observable<number> {
    return this.baseService.patch('quote/update-order-status/' + orderModel.id, orderModel)
      .pipe(map<any, number>(response => {
          orderModel = response.data as any;
          return orderModel.id;
        }),
        catchError(err => throwError(() => err))
      );
  }

  changeQuoteProductStatus(orderModel: any): Observable<number> {
    return this.baseService.patch('quote-product/update-status/' + orderModel.id, orderModel)
      .pipe(map<any, number>(response => {
          orderModel = response.data as any;
          return orderModel.id;
        }),
        catchError(err => throwError(() => err))
      );
  }
}
